import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';
import {
  provideRouter,
  RouteReuseStrategy,
  TitleStrategy,
  withComponentInputBinding,
} from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { Store, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideOAuthClient, OAuthService } from 'angular-oauth2-oidc';
import { filter } from 'rxjs';
import { routes } from './app.routes';
import { reducers } from './store';
import { AuthActions } from './core/auth/store/auth.actions';
import { AuthEffects } from './core/auth/store/auth.effects';
import { UserContextEffects } from './core/user-context/store/user-context.effects';
import { resolveIdentity } from './core/auth/identity.util';
import { GlobalErrorHandler } from './core/errors/global-error.handler';
import { authConfigFactory } from './core/auth/auth-config.factory';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { orgHeaderInterceptor } from './core/interceptors/org-header.interceptor';
import { loadingInterceptor } from './core/interceptors/loading.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { PageTitleStrategy } from './core/services/page-title.strategy';
import { OrgSwitchReuseStrategy } from './core/navigation/org-switch-reload';
import { environment } from '../environments/environment';

function initializeOAuth(oauthService: OAuthService): () => void {
  return (): void => {
    oauthService.configure(authConfigFactory());
    // App-wide token refresh. The expiration timer + refresh must live at the
    // root (not in CallbackComponent, which is destroyed right after login), so
    // the session refreshes for its whole lifetime. For the PKCE code flow this
    // calls refreshToken() on `token_expires` automatically.
    oauthService.setupAutomaticSilentRefresh();
  };
}

function initializeSession(store: Store, oauthService: OAuthService): () => void {
  return (): void => {
    // Rehydrate the session on a warm start (a token already exists from a prior
    // login). CallbackComponent only runs on the post-login redirect, so on a
    // plain refresh nothing would otherwise repopulate the auth store — leaving
    // the shell without the user's name/email. Replay loginSuccess from the
    // persisted token's claims; the orgGuard then resolves the org + UserContext
    // when the shell activates. On a cold start there is no token yet, so we do
    // nothing (the fresh login flows through CallbackComponent instead).
    if (oauthService.hasValidAccessToken()) {
      const identity = resolveIdentity(oauthService.getIdentityClaims());
      store.dispatch(
        AuthActions.loginSuccess({
          token: oauthService.getAccessToken(),
          expiresAt: oauthService.getAccessTokenExpiration(),
          ...identity,
        }),
      );
    }
  };
}

function initializeRefreshFailureLogout(store: Store, oauthService: OAuthService): () => void {
  return (): void => {
    // If a silent refresh fails, fail closed: the session can no longer be
    // renewed, so log out rather than letting the next API call 401 unhandled.
    oauthService.events
      .pipe(filter((event) => event.type === 'token_refresh_error'))
      .subscribe(() => store.dispatch(AuthActions.logout({ endType: 'TIMEOUT' })));
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideRouter(routes, withComponentInputBinding()),
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    // Re-creates the open page after an org switch (OrgSwitchReloadService).
    { provide: RouteReuseStrategy, useExisting: OrgSwitchReuseStrategy },
    // Interceptor order: auth token → org header → loading counter → error handling.
    provideHttpClient(
      withInterceptors([
        authInterceptor,
        orgHeaderInterceptor,
        loadingInterceptor,
        errorInterceptor,
      ]),
    ),
    provideOAuthClient(),
    provideStore(reducers),
    provideEffects([AuthEffects, UserContextEffects]),
    // Ship the NgRx devtools instrumentation only in non-production builds.
    ...(environment.production ? [] : [provideStoreDevtools({ maxAge: 25 })]),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Outlined icon set app-wide (mat-icon defaults to Material Icons Outlined).
    { provide: MAT_ICON_DEFAULT_OPTIONS, useValue: { fontSet: 'material-icons-outlined' } },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeOAuth,
      deps: [OAuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeSession,
      deps: [Store, OAuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeRefreshFailureLogout,
      deps: [Store, OAuthService],
      multi: true,
    },
  ],
};
